import { useTranslation } from "react-i18next";

import { Arrange, Typography, Spacer } from "@aviary/components";
import { l } from "@aviary/locales/i18n";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { Container } from "../Container";

import { FooterIcons } from "./FooterIcons";
import { FooterLinkList } from "./FooterLinkList";
import type { PageFooterProps } from "./PageFooter.types";

import * as styles from "./PageFooter.styles";

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/layouts/components/PageFooter/
 */
const PageFooter = ({
  type = "basic",
  isWholesale,
  customPrefooter,
  withSidebar,
  hideLinkList,
  hideShippingDisclaimer,
}: PageFooterProps) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();
  const { desktop } = useBreakpoints();

  const footerStyles = [styles.footerBase];

  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  const renderFullscriptCopyright = () => (
    <Typography type="footnote">
      &copy; {t(l.aviary.footer.FullscriptCopyRights, { currentYear: getCurrentYear() })}
    </Typography>
  );

  const renderFDAdisclaimer = () => {
    if (!isUS) return null;

    return (
      <div css={styles.fdaDisclaimer}>
        <Typography type="footnote">{t(l.aviary.footer.FDAdisclaimerLine1)}</Typography>
        {type !== "labs" && (
          <Typography type="footnote">{t(l.aviary.footer.FDAdisclaimerLine2)}</Typography>
        )}
      </div>
    );
  };

  const renderShippingDisclaimer = () => {
    if (hideShippingDisclaimer) return null;

    return (
      <div css={styles.fdaDisclaimer}>
        <Typography type="footnote">{t(l.aviary.footer.ShippingDisclaimer)}</Typography>
      </div>
    );
  };

  const renderLeftLayoutContent = () => {
    if (type === "checkout") {
      return renderShippingDisclaimer();
    }

    return renderFDAdisclaimer();
  };

  const renderLinkList = () => {
    if (hideLinkList) return null;

    return <FooterLinkList type={type} isWholesale={isWholesale} />;
  };

  const renderMobileLayout = () => {
    if (type !== "basic") {
      return (
        <Arrange isVertical isFullWidth justify="center" css={styles.mobileLayout}>
          <FooterIcons type={type} />
          {renderLeftLayoutContent()}
          {renderLinkList()}
          {renderFullscriptCopyright()}
        </Arrange>
      );
    }

    return (
      <Arrange isVertical alignment="center">
        {renderLinkList()}
        {renderFullscriptCopyright()}
      </Arrange>
    );
  };

  const renderDesktopLayout = () => {
    if (type !== "basic") {
      return (
        <Arrange justify="spaceBetween" alignment="center" isWrapped={false}>
          <Arrange isVertical isFullWidth>
            {renderLeftLayoutContent()}
            <Arrange isWrapped={false} alignment="center">
              {renderFullscriptCopyright()}
              {renderLinkList()}
            </Arrange>
          </Arrange>
          <div>
            <FooterIcons type={type} />
          </div>
        </Arrange>
      );
    }

    return (
      <Arrange justify="spaceBetween" alignment="center">
        {renderFullscriptCopyright()}
        {renderLinkList()}
      </Arrange>
    );
  };

  const renderPreFooter = () => {
    if (!customPrefooter) return null;

    return (
      <>
        {customPrefooter}
        <Spacer type="section" />
      </>
    );
  };

  return (
    <footer css={footerStyles}>
      <Spacer type="footer" />
      <Container withSidebar={withSidebar}>
        {renderPreFooter()}
        {desktop.lessThan ? renderMobileLayout() : renderDesktopLayout()}
      </Container>
      <Spacer type="footer" />
      {/* Height added in aviaryNormalize.styles.ts when StickyFooter is mounted */}
      <div data-footer="sticky" />
    </footer>
  );
};

export { PageFooter };
